import { QueryClientProvider, QueryClient } from 'react-query';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import { ReactQueryDevtools } from 'react-query/devtools';
import { createMemoryRouter, RouterProvider, Outlet } from 'react-router-dom';
import { node, bool } from 'prop-types';
import { unitTestOptions } from '../shared/DataFetchingWrapper';
import { updateMockSettings } from './handlers';
import { defaultStoryArgs } from './defaultStoryArgs';
import { staticIdentityRoutes } from '../components/Identity/IdentityRoutes';

/**
 * Wrapper component for Storybook that turns on/off mocks according to the
 * passed parameter.
 */
export const StorybookClient = ({ disableMocks = false, children }) => {
	if (disableMocks) {
		import('./browser').then(({ worker }) => worker.stop());
	}
	// Use the same query options as the actual app
	const queryClient = new QueryClient(
		process.env.NODE_ENV === 'test' ? unitTestOptions : {}
	);
	return (
		<QueryClientProvider client={queryClient}>
			{children}
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	);
};

const dynamicRouter = (Story, { args, parameters }) => {
	const router = createMemoryRouter(
		[
			{
				path: '/',
				element: (
					<ErrorBoundary
						key="error-boundary-1"
						FallbackComponent={() => <div>App will handle the error case</div>}
					>
						<Outlet />
					</ErrorBoundary>
				),
				id: 'identity',
				children: [...staticIdentityRoutes, parameters.mockRoute || {}],
				loader: () => ({
					apiEnvironment: args.apiEnvironment || 'dev',
					blob: args.blob || 'string',
					clientId: args.clientId || 'ashlink.code',
					customGenericErrorMessage: args.customGenericErrorMessage,
					customerServicePhoneNumber:
						args.customerServicePhoneNumber || '123-456-7890',
					customerServicePhoneNumberFitness:
						args.customerServicePhoneNumber || '987-654-3210',
					customerServiceOptionForFitness: args.customerServiceOptionForFitness,
					duplicateCheckEnabled: args.duplicateCheckEnabled || false,
					eligibilityPrepopulation: args.eligibilityPrepopulation || null,
					eligibilityURL: args.eligibilityURL || '/testEligibilityURL',
					enableSSO: args.enableSSO || false,
					hasOwnProvider: !args.hasOwnProvider || true,
					ineligibleRegistration: args.ineligibleRegistration || false,
					loginHostingPath: args.loginHostingPath,
					logOutLink: args.logOutLink || '/testLogOutLink',
					overrideMainTitleTo: args.overrideMainTitleTo,
					pageType: args.pageType,
					path: args.path || 1,
					redirectUri: args.redirectUri || 'https://dev.ashlink.com/callback',
					registrationFormLanguage: args.registrationFormLanguage || null,
					showGenderField: args.showGenderField || false,
					siteTitle: args.siteTitle || ' | Silver & Fit',
					ssId: args.ssId === '0' ? null : args.ssId || 'fakeSSId',
					termsURL: args.termsURL || '/test-terms',
					contactURL: args.contactURL || '/test-contact-us',
					unauthorizedCallback:
						args.unauthorizedCallback ||
						(() => console.log('unauthorizedCallback called')),
					...(args.loaderProps || {}),
				}),
			},
		],
		{
			initialEntries: args.initialEntries || ['/'],
			initialIndex: args.initialIndex || 0,
		}
	);
	return (
		<RouterProvider router={router}>
			<Story {...args} />
		</RouterProvider>
	);
};

export const helmetDecorator = Story => (
	<HelmetProvider>
		<Story />
	</HelmetProvider>
);

/**
 * This decorator updates our mock settings. The story must add a `mockSettings` property to its
 * `parameters` object.
 */
export const MockSettings = (Story, context) => {
	const {
		parameters: { mockSettings },
		args,
	} = context;
	updateMockSettings({ ...mockSettings, ...args } || {});
	const clientArgs = { ...defaultStoryArgs, ...args };

	return (
		<StorybookClient disableMocks={clientArgs.disableMocks}>
			<Story />
		</StorybookClient>
	);
};

export const globalDecorators = [dynamicRouter, MockSettings, helmetDecorator];

StorybookClient.propTypes = {
	disableMocks: bool,
	children: node.isRequired,
};
